import type { Rgba } from '../sim/color.ts';
import {
  createEmptySettings,
  parseSettings,
  pushRecentColor,
  pushRecentMap,
  serializeSettings,
  type EditorSettings,
  type RecentMap,
  type RenderSettings,
  type SettingsDocument,
} from '../sim/settings/index.ts';

const STORAGE_KEY = 'battleboats:settings';

function readDocument(): SettingsDocument {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return createEmptySettings();
    }

    return parseSettings(raw) ?? createEmptySettings();
  } catch {
    return createEmptySettings();
  }
}

function writeDocument(document: SettingsDocument): void {
  try {
    localStorage.setItem(STORAGE_KEY, serializeSettings(document));
  } catch {
    // Ignore storage failures; recents and editor chrome are best-effort.
  }
}

export function loadEditorSettings(): EditorSettings {
  return readDocument().editor;
}

/** Read at boot by `createGameConfig`; the WebGL context cannot change afterwards. */
export function loadRenderSettings(): RenderSettings {
  return readDocument().render;
}

export function patchRenderSettings(patch: Partial<RenderSettings>): RenderSettings {
  const document = readDocument();
  document.render = { ...document.render, ...patch };
  writeDocument(document);
  return document.render;
}

export function patchEditorSettings(patch: Partial<EditorSettings>): EditorSettings {
  const document = readDocument();
  document.editor = { ...document.editor, ...patch };
  writeDocument(document);
  return document.editor;
}

export function loadRecentColors(): Rgba[] {
  return readDocument().recentColors;
}

export function recordRecentColor(incoming: Rgba): Rgba[] {
  const document = readDocument();
  document.recentColors = pushRecentColor(document.recentColors, incoming);
  writeDocument(document);
  return document.recentColors;
}

export function recordRecentMap(id: string, label: string): RecentMap[] {
  const document = readDocument();
  document.editor.recentMaps = pushRecentMap(document.editor.recentMaps, { id, label });
  writeDocument(document);
  return document.editor.recentMaps;
}

export function dropRecentMap(id: string): RecentMap[] {
  const document = readDocument();
  document.editor.recentMaps = document.editor.recentMaps.filter((entry) => entry.id !== id);
  writeDocument(document);
  return document.editor.recentMaps;
}
